import { useState, useEffect } from 'react'

const NotepadApp = () => {
  const [content, setContent] = useState('')
  const [fileName, setFileName] = useState('Untitled.txt')
  const [isModified, setIsModified] = useState(false)
  // Only consulted below the sm breakpoint, where the sidebar and the editor
  // take turns instead of sitting side by side.
  const [showFiles, setShowFiles] = useState(false)
  const [savedFiles, setSavedFiles] = useState(() => {
    const saved = localStorage.getItem('notepad-files')
    return saved ? JSON.parse(saved) : []
  })

  const saveToLocalStorage = (files) => {
    localStorage.setItem('notepad-files', JSON.stringify(files))
  }

  const handleContentChange = (e) => {
    setContent(e.target.value)
    setIsModified(true)
  }

  const saveFile = () => {
    const newFile = {
      name: fileName,
      content: content,
      lastModified: new Date().toLocaleString()
    }
    
    const updatedFiles = [...savedFiles.filter(f => f.name !== fileName), newFile]
    setSavedFiles(updatedFiles)
    saveToLocalStorage(updatedFiles)
    setIsModified(false)
  }

  const openFile = (file) => {
    if (isModified) {
      const confirmOpen = window.confirm('You have unsaved changes. Do you want to continue?')
      if (!confirmOpen) return
    }
    
    setContent(file.content)
    setFileName(file.name)
    setIsModified(false)
    setShowFiles(false) // On narrow screens, land the user back in the editor.
  }

  const newFile = () => {
    if (isModified) {
      const confirmNew = window.confirm('You have unsaved changes. Do you want to continue?')
      if (!confirmNew) return
    }
    
    setContent('')
    setFileName('Untitled.txt')
    setIsModified(false)
    setShowFiles(false) // On narrow screens, land the user back in the editor.
  }

  const deleteFile = (fileToDelete) => {
    if (window.confirm(`Are you sure you want to delete "${fileToDelete.name}"?`)) {
      const updatedFiles = savedFiles.filter(f => f.name !== fileToDelete.name)
      setSavedFiles(updatedFiles)
      saveToLocalStorage(updatedFiles)
      
      if (fileName === fileToDelete.name) {
        newFile()
      }
    }
  }

  const downloadFile = () => {
    const element = document.createElement('a')
    const file = new Blob([content], { type: 'text/plain' })
    element.href = URL.createObjectURL(file)
    element.download = fileName
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <div className="flex h-full">
      {/* Sidebar. Wide screens show it beside the editor. Narrow screens have no
          room for both, so it becomes a pane you switch to and back from: the
          file controls are essential, so hiding them outright is not an option. */}
      <div
        className={`${showFiles ? 'flex' : 'hidden'} sm:flex flex-col w-full sm:w-64 bg-white/5 rounded-lg p-3 sm:mr-4`}
      >
        <div className="flex items-center justify-between mb-3 sm:hidden">
          <h3 className="text-sm font-semibold text-white/80">Files</h3>
          <button
            onClick={() => setShowFiles(false)}
            className="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-sm"
          >
            Back to editor
          </button>
        </div>

        <div className="mb-4">
          <button
            onClick={newFile}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded mb-2 text-sm"
          >
            <i className="fas fa-file mr-2"></i>
            New File
          </button>
          <button
            onClick={saveFile}
            disabled={!isModified}
            className={`w-full py-2 px-3 rounded text-sm ${
              isModified 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            <i className="fas fa-save mr-2"></i>
            Save File
          </button>
        </div>
        
        <div className="mb-3">
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="w-full bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            placeholder="File name"
          />
        </div>
        
        <h3 className="text-sm font-semibold mb-2 text-white/80">
          <i className="fas fa-folder mr-2"></i>
          Saved Files ({savedFiles.length})
        </h3>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {savedFiles.map((file, index) => (
            <div
              key={index}
              className={`p-2 rounded text-xs cursor-pointer transition-colors ${
                fileName === file.name 
                  ? 'bg-blue-600/30 border border-blue-400/50' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <span 
                  className="flex-1 truncate" 
                  onClick={() => openFile(file)}
                >
                  <i className="fas fa-file-text mr-2 text-blue-400"></i>
                  {file.name}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteFile(file)
                  }}
                  className="ml-2 text-red-400 hover:text-red-300"
                >
                  <i className="fas fa-trash text-xs"></i>
                </button>
              </div>
              <div className="text-white/50 mt-1">
                {file.lastModified}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Editor */}
      <div className={`${showFiles ? 'hidden' : 'flex'} sm:flex flex-1 flex-col min-w-0`}>
        {/* Editor Header */}
        <div className="flex items-center justify-between mb-3 p-2 bg-white/5 rounded gap-3">
          <div className="flex items-center space-x-3 min-w-0">
            <button
              onClick={() => setShowFiles(true)}
              className="sm:hidden shrink-0 px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-sm"
              aria-label={`Show saved files, ${savedFiles.length} saved`}
            >
              <i className="fas fa-folder mr-2"></i>
              {savedFiles.length}
            </button>
            <span className="text-sm font-medium truncate">
              {fileName} {isModified && <span className="text-yellow-400">*</span>}
            </span>
            <span className="hidden sm:inline text-xs text-white/60 shrink-0">
              {content.length} characters
            </span>
          </div>
          <button
            onClick={downloadFile}
            className="bg-purple-600 hover:bg-purple-700 text-white py-1 px-3 rounded text-sm"
          >
            <i className="fas fa-download mr-2"></i>
            Download
          </button>
        </div>
        
        {/* Text Area */}
        <textarea
          value={content}
          onChange={handleContentChange}
          className="flex-1 bg-white/5 rounded-lg p-4 text-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono text-sm"
          placeholder="Start typing your note..."
        />
      </div>
    </div>
  )
}

export default NotepadApp